import React, { useState, useEffect } from 'react';
import config from 'config';
import { sendIdentityServiceAuthenticatedRequest } from '../../services/AsyncRequestService';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import FormLabel from 'react-bootstrap/FormLabel';
import FormGroup from 'react-bootstrap/FormGroup';
import FormControl from 'react-bootstrap/FormControl';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { getErrorMessage } from 'services/UtilityService';
import PasswordValidationFeedback from 'components/User/PasswordValidationFeedback';
import { updateDeviceId, updateCsrfToken } from 'services/AuthService';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordPolicy, setPasswordPolicy] = useState({});
  const [requestInProgress, setRequestInProgress] = useState(false);

  useEffect(() => {
    fetchPasswordPolicy();
  }, []);

  const fetchPasswordPolicy = () => {
    setRequestInProgress(true);
    sendIdentityServiceAuthenticatedRequest(
      `/auth/password-policy`,
      'GET',
      null,
      (response) => {
        const { data } = response;
        setPasswordPolicy(data);
        setRequestInProgress(false);
      },
      (error) => {
        const data = getErrorMessage(error.data) || getErrorMessage('PASSWORD_POLICY_NOT_FETCHED');
        setError(data);
        setRequestInProgress(false);
      },
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'currentPassword') {
      setCurrentPassword(value);
    } else if (name === 'newPassword') {
      setNewPassword(value);
    } else if (name === 'newPasswordConfirm') {
      setNewPasswordConfirm(value);
    }
    setError('');
  };

  const handleChangePassword = (e) => {
    e.preventDefault();

    let error = '';
    if (!currentPassword) {
      error = 'Current password cannot be empty.';
    } else if (!newPassword) {
      error = 'New password cannot be empty.';
    } else if (newPassword !== newPasswordConfirm) {
      error = 'Passwords do not match.';
    }

    if (error) {
      setError(error);
      return;
    }

    let url = '/auth/change-password';
    let method = 'POST';
    let data = { previousPassword: currentPassword, proposedPassword: newPassword };
    setError('');
    setSuccess('');
    setRequestInProgress(true);
    sendIdentityServiceAuthenticatedRequest(url, method, data, onChangePasswordSuccess, onChangePasswordFailure);
  };

  const onChangePasswordSuccess = (response) => {
    setRequestInProgress(false);
    setCurrentPassword('');
    setNewPassword('');
    setNewPasswordConfirm('');
    const data = response.data;

    updateDeviceId(data.deviceKey);
    updateCsrfToken(data.csrfToken);
    setSuccess('Password changed successfully');
  };

  const onChangePasswordFailure = (error) => {
    setRequestInProgress(false);
    setError(getErrorMessage(error.data) || getErrorMessage('PASSWORD_CHANGE_FAILED'));
  };

  return (
    <div className="change-password-page">
      <Container fluid>
        <Row>
          <Col md={{ span: 6, offset: 3 }}>
            <div className="change-password-box">
              <Card className="big-top-spacer" variant="primary">
                <Card.Body>
                  <Card.Title className="text-center">Change Password</Card.Title>
                  <Alert variant="info" className="mb-3">
                    After changing your password, all other sessions (e.g. other browsers or devices) will be logged out. You will stay signed in on this device.
                  </Alert>
                  {error ? <Alert variant="danger">{error}</Alert> : null}
                  {success ? <Alert variant="success">{success}</Alert> : null}
                  <form onSubmit={handleChangePassword}>
                    <FormGroup>
                      <FormLabel>Current Password</FormLabel>
                      <FormControl
                        type="password"
                        name="currentPassword"
                        value={currentPassword}
                        placeholder="Enter current password"
                        onChange={handleChange}
                        disabled={requestInProgress}
                      />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>New Password</FormLabel>
                      <FormControl
                        type="password"
                        name="newPassword"
                        value={newPassword}
                        placeholder="Enter new password"
                        onChange={handleChange}
                        disabled={requestInProgress}
                      />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>Confirm New Password</FormLabel>
                      <FormControl
                        type="password"
                        name="newPasswordConfirm"
                        value={newPasswordConfirm}
                        placeholder="Enter new password again"
                        onChange={handleChange}
                        disabled={requestInProgress}
                      />
                    </FormGroup>
                    <hr />
                    <Button type="submit" variant="primary" disabled={requestInProgress} block>
                      Change Password
                    </Button>
                  </form>
                  <PasswordValidationFeedback passwordPolicy={passwordPolicy} password={newPassword} confirmedPassword={newPasswordConfirm} />
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ChangePassword;
